import { useEffect, useRef, useState } from 'react';
import { useLocale } from '../i18n';

/**
 * A finger-drawn signature on a canvas (pointer events, so mouse, pen and touch all work). Reports a
 * PNG data URL whenever a stroke ends, null when cleared. Drawn at device pixel ratio so it prints clean.
 */
export function SignaturePad({ onChange, label }: { onChange: (png: string | null) => void; label: string }) {
  const { t } = useLocale();
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const moved = useRef(false); // a bare tap is not a signature
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    const ratio = window.devicePixelRatio || 1;
    const w = c.clientWidth || 320;
    const h = c.clientHeight || 140;
    c.width = Math.round(w * ratio);
    c.height = Math.round(h * ratio);
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#12233A';
  }, []);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const down = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = canvas.current?.getContext('2d');
    if (!ctx) return;
    if (typeof e.currentTarget.setPointerCapture === 'function') e.currentTarget.setPointerCapture(e.pointerId);
    drawing.current = true;
    const p = point(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvas.current?.getContext('2d');
    if (!ctx) return;
    const p = point(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    moved.current = true;
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (!moved.current) return;
    setSigned(true);
    const c = canvas.current;
    if (c) onChange(c.toDataURL('image/png'));
  };
  const clear = () => {
    const c = canvas.current;
    const ctx = c?.getContext('2d');
    if (c && ctx) ctx.clearRect(0, 0, c.width, c.height);
    moved.current = false;
    setSigned(false);
    onChange(null);
  };

  return (
    <div className="signature-pad stack-sm">
      <span className="label">{label}</span>
      <canvas ref={canvas} className="signature-canvas" aria-label={label} role="img" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onPointerLeave={up} />
      <div className="contact-actions">
        <span className="muted">{signed ? `✓ ${t('signed')}` : t('signHere')}</span>
        <button type="button" className="btn btn-ghost" onClick={clear}>
          {t('clearSignature')}
        </button>
      </div>
    </div>
  );
}
